import React, { useEffect, useRef, useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import TooltipText from "../common/tooltipText";
import "../../styles/admin/modelCard.scss";
import OpenaiIcon from "../../styles/assets/svg/openai.svg";
import GeminiIcon from "../../styles/assets/svg/gemini.svg";
import ClaudeIcon from "../../styles/assets/svg/claude.svg";
import AzureIcon from "../../styles/assets/svg/azure.svg";

const ModelCard = ({ model, onClick, onEdit, onDelete, onToggleActive }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const getAvatarClassByProvider = (providerRaw) => {
    const provider = String(providerRaw || "").toLowerCase();
    if (provider === "openai") return "model-card__avatar--openai";
    if (provider === "gemini") return "model-card__avatar--gemini";
    if (provider === "claude") return "model-card__avatar--claude";
    if (provider === "azure") return "model-card__avatar--azure";
    return "";
  };

  const getProviderIcon = (providerRaw) => {
    const provider = String(providerRaw || "").toLowerCase();
    if (provider === "gemini") return { src: GeminiIcon, alt: "Gemini" };
    if (provider === "claude") return { src: ClaudeIcon, alt: "Claude" };
    if (provider === "azure") return { src: AzureIcon, alt: "Azure" };
    return { src: OpenaiIcon, alt: "OpenAI" };
  };

  const handleClick = (e) => {
    if (showMenu) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick(model);
    }
  };

  const handleMenuToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(false);
    onEdit && onEdit(model);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMenu(false);
    onDelete && onDelete(model);
  };

  const formatCreatedAt = (value) => {
    if (!value) return "N/A";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    } catch (e) {
      return String(value);
    }
  };

  const providerIcon = getProviderIcon(model?.provider);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div className="model-card" onClick={handleClick}>
      <div className="model-card__title-row">
        <div
          className={`model-card__avatar ${getAvatarClassByProvider(
            model?.provider
          )}`}
        >
          <span className="model-card__avatar-icon">&lt;&gt;</span>
        </div>

        <div className="model-card__title-block">
          <TooltipText
            text={model?.name || "Untitled Model"}
            className="model-card__title"
          />
          <span className="model-card__subtitle">MODEL</span>
        </div>

        <div className="model-card__title-right">
          <PowerSettingsNewIcon
            fontSize="small"
            className={
              model?.is_active
                ? "model-card__status-icon model-card__status-icon--active"
                : "model-card__status-icon model-card__status-icon--inactive"
            }
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onToggleActive) {
                onToggleActive(model);
              }
            }}
          />

          {(onEdit || onDelete || onToggleActive) && (
            <div className="model-card__actions" ref={menuRef}>
              <button
                type="button"
                className="model-card__action-btn"
                onClick={handleMenuToggle}
                aria-haspopup="menu"
                aria-expanded={showMenu}
                aria-label="Model actions"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <MoreVertIcon fontSize="small" />
              </button>

              {showMenu && (
                <div
                  className="model-card__actions-menu"
                  role="menu"
                  onClick={(e) => e.stopPropagation()}
                >
                  {onEdit && (
                    <button
                      type="button"
                      className="model-card__actions-item"
                      onClick={handleEdit}
                      role="menuitem"
                    >
                      <span className="model-card__actions-item-label">
                        <EditIcon fontSize="small" /> Edit
                      </span>
                    </button>
                  )}

                  {onDelete && (
                    <button
                      type="button"
                      className="model-card__actions-item model-card__actions-item--danger"
                      onClick={handleDelete}
                      role="menuitem"
                    >
                      <span className="model-card__actions-item-label">
                        <DeleteIcon fontSize="small" /> Delete
                      </span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="model-card__meta-grid">
        <div className="model-card__meta-item">
          <div className="model-card__meta-pill">
            <img
              src={providerIcon.src}
              alt={providerIcon.alt}
              className="model-card__meta-pill-icon"
            />
          </div>
          <div className="model-card__meta-text">
            <span className="model-card__meta-label">Provider</span>
            <span className="model-card__meta-value">
              {model?.provider || "--"}
            </span>
          </div>
        </div>

        <div className="model-card__meta-item">
          <div className="model-card__meta-pill">
            <span className="model-card__meta-pill-icon">&lt;&gt;</span>
          </div>
          <div className="model-card__meta-text">
            <span className="model-card__meta-label">Model type</span>
            <TooltipText
              text={model?.model_name || model?.type || "--"}
              className="model-card__meta-value"
              fullWidth
            />
          </div>
        </div>
      </div>

      <div className="model-card__footer">
        <span className="model-card__footer-item">
          <TooltipText
            text={`Created : ${formatCreatedAt(model?.created_at)}`}
            fullWidth
            placement="bottom"
          />
        </span>
      </div>
    </div>
  );
};

export default ModelCard;
